/**
 * Use language and country codes to select proper locale based on region.
 */
const localeMapper: Map<string, string> = new Map<string, string>([
    ["en_AU", "en-GB"], // British English (Australia)
    ["en_GB", "en-GB"], // British English (Great Britain)
    ["zh_CN_CN", "zh-Hans"], // Simplified Chinese (China)
    ["zh_TW_HK", "zh-Hant-HK"], // Traditional Chinese (Hong Kong)
    ["zh_TW_TW", "zh-Hant"] // Traditional Chinese (Taiwan)
]);

/**
 * <p>
 *     <strong>Language Value Object</strong>
 * </p>
 *
 * <p>
 *     The <code>LanguageVO</code> class represents a locale and must be used to specify the current language and
 *     locale that will be used by the localization setService.
 * </p>
 *
 * <p>
 *    This class accept as parameters the <strong>GGS Country Code</strong> and <strong>GGS Language Code</strong>
 *    standards.
 * </p>
 *
 */
export class LanguageVO {
    private _locale: string;

    /**
     * Value Object used to inform the current language and locale of the user.
     *
     * @param ggsCountryCode Country code respecting the <strong>GGS Country Code</strong> standard.   (DE  or TW)
     * @param ggsLanguageCode Language code respecting the <strong>GGS Language Code</strong> standard. (de or zh_TW)
     * @param neverUseAbbreviations Specify if the processor should abbreviate numbers.
     * @param abbreviationThreshold Specify the abbreviation threshold used to determine if a number will be compressed or not.
     * @param fractionalDigits Specify the maximum number of digits that can appear after the decimal separator.
     * @param leadingZero Specifies whether a leading zero is included in a formatted number
     * when there are no integer digits to the left of the decimal separator.
     * @param trailingZeros Specifies whether trailing zeros are included in a formatted number.
     * @default false.
     */
    constructor(
        public readonly ggsCountryCode: string,
        public readonly ggsLanguageCode: string,
        public readonly neverUseAbbreviations: boolean = false,
        public readonly abbreviationThreshold: number = 100000,
        public readonly fractionalDigits: number = -1,
        public readonly leadingZero: boolean = true,
        public readonly trailingZeros: boolean = true
    ) {
        let locale: string = `${this.ggsLanguageCode}_${this.ggsCountryCode.toUpperCase()}`;

        this._locale = localeMapper.has(locale) ? localeMapper.get(locale) : this.ggsLanguageCode.replace(/_\w+/g, "");
    }

    /**
     * Return if the current language is written from right to left.
     */
    public get isLanguageWrittenRightToLeft(): boolean {
        return this.ggsLanguageCode === "ar";
    }

    public get locale(): string {
        return this._locale;
    }
}
